/** Photos are sent at most this wide or tall; phones take far larger ones than a website needs. */
const MAX_EDGE = 2000;
/** The AI copy, which only the admin and designer look at, is at most this wide or tall. */
const AI_COPY_EDGE = 1024;
/** Uploads are refused above 5 MB; prepared photos stay well under it. */
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const SMALL_PNG_BYTES = 1.5 * 1024 * 1024;

export class PhotoError extends Error {}

export interface PreparedPhoto {
  /** What the site shows. */
  photo: Blob;
  /** A small JPEG for the admin to look at; never shown on the site. Null when it couldn't be made. */
  aiCopy: Blob | null;
}

async function jpeg(bitmap: ImageBitmap, maxEdge: number, quality: number, background?: string): Promise<Blob | null> {
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const context = canvas.getContext("2d");
  if (!context) return null;
  // JPEG has no transparency: a logo's clear background would otherwise turn black.
  if (background) {
    context.fillStyle = background;
    context.fillRect(0, 0, canvas.width, canvas.height);
  }
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
}

/** SVGs over this are refused by the server; logos are far smaller. */
const MAX_SVG_BYTES = 1024 * 1024;

/** Loads an SVG so a canvas can draw it; without a width and height of its own, its viewBox gives the size. */
async function svgImage(file: Blob): Promise<{ image: HTMLImageElement; width: number; height: number }> {
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => { image.onload = () => resolve(); image.onerror = () => reject(new Error("SVG unavailable")); image.src = url; });
    if (image.naturalWidth && image.naturalHeight) return { image, width: image.naturalWidth, height: image.naturalHeight };
    const box = /viewBox\s*=\s*["']\s*[-\d.]+[\s,]+[-\d.]+[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(await file.text());
    return { image, width: Number(box?.[1]) || 512, height: Number(box?.[2]) || 512 };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Prepares an SVG (usually a logo). The SVG itself is what the site shows, sharp at any size, and
 * the server checks it. The admin can't see SVG, so the AI copy is drawn from it on white at 1024px;
 * an SVG photo is only accepted with one.
 */
async function prepareSvg(file: File): Promise<PreparedPhoto> {
  if (file.size > MAX_SVG_BYTES) throw new PhotoError("This SVG is too large to upload.");
  let loaded: Awaited<ReturnType<typeof svgImage>>;
  try { loaded = await svgImage(file); } catch { throw new PhotoError("This SVG can’t be read here. Try a PNG instead."); }
  const scale = AI_COPY_EDGE / Math.max(loaded.width, loaded.height);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(loaded.width * scale));
  canvas.height = Math.max(1, Math.round(loaded.height * scale));
  const context = canvas.getContext("2d");
  if (!context) throw new PhotoError("This SVG couldn’t be prepared.");
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(loaded.image, 0, 0, canvas.width, canvas.height);
  const aiCopy = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
  if (!aiCopy || aiCopy.size > MAX_UPLOAD_BYTES) throw new PhotoError("This SVG couldn’t be prepared.");
  return { photo: file.type === "image/svg+xml" ? file : new Blob([file], { type: "image/svg+xml" }), aiCopy };
}

const isSvg = (file: File) => file.type === "image/svg+xml" || (!file.type && /\.svg$/i.test(file.name));

/**
 * Prepares a photo for upload in the browser: turned the right way up, scaled down to a sensible
 * size and re-encoded as JPEG, which also drops camera metadata such as location. A small PNG
 * (often a logo with a transparent background) is kept as it is, and an SVG stays an SVG. Also
 * makes the AI copy: a JPEG at most 1024px, for the admin to look at.
 */
export async function preparePhoto(file: File): Promise<PreparedPhoto> {
  if (isSvg(file)) return await prepareSvg(file);
  const keep = file.type === "image/png" && file.size <= SMALL_PNG_BYTES;
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    if (keep) return { photo: file, aiCopy: null };
    throw new PhotoError("This photo type can’t be read here. Try a JPEG or PNG.");
  }
  try {
    const photo = keep ? file : await jpeg(bitmap, MAX_EDGE, 0.86);
    if (!photo) throw new PhotoError("This photo couldn’t be prepared.");
    if (photo.size > MAX_UPLOAD_BYTES) throw new PhotoError("This photo is too large to upload.");
    const aiCopy = await jpeg(bitmap, AI_COPY_EDGE, 0.8, "#ffffff").catch(() => null);
    return { photo, aiCopy: aiCopy && aiCopy.size <= MAX_UPLOAD_BYTES ? aiCopy : null };
  } finally {
    bitmap.close();
  }
}

/**
 * Prepares an inspiration image the owner shares in the chat: only the AI copy is needed (a JPEG at
 * most 1024px, turned the right way up, without camera metadata), since it never goes on the site.
 */
export async function prepareReference(file: File): Promise<Blob> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new PhotoError("This image type can’t be read here. Try a JPEG or PNG.");
  }
  try {
    const copy = await jpeg(bitmap, AI_COPY_EDGE, 0.8, "#ffffff");
    if (!copy || copy.size > MAX_UPLOAD_BYTES) throw new PhotoError("This image couldn’t be prepared.");
    return copy;
  } finally {
    bitmap.close();
  }
}

/** Browser-tab icons are square; this size stays sharp on high-density screens and home screens. */
const ICON_EDGE = 256;

/** Prepares a site icon: the centre square of the image, scaled to 256px, as a PNG so transparency survives. An SVG stays an SVG. */
export async function prepareIcon(file: File): Promise<Blob> {
  // Browsers draw an SVG icon at any size, so it is kept as it is (the server checks it).
  if (isSvg(file)) {
    if (file.size > MAX_SVG_BYTES) throw new PhotoError("This SVG is too large to upload.");
    return file.type === "image/svg+xml" ? file : new Blob([file], { type: "image/svg+xml" });
  }
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new PhotoError("This image type can’t be read here. Try a PNG or JPEG.");
  }
  try {
    const side = Math.min(bitmap.width, bitmap.height);
    const edge = Math.min(ICON_EDGE, side);
    const canvas = document.createElement("canvas");
    canvas.width = edge;
    canvas.height = edge;
    const context = canvas.getContext("2d");
    if (!context) throw new PhotoError("This image couldn’t be prepared.");
    context.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, edge, edge);
    const icon = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!icon || icon.size > MAX_UPLOAD_BYTES) throw new PhotoError("This image couldn’t be prepared.");
    return icon;
  } finally {
    bitmap.close();
  }
}

/** Link previews are 1.91:1; this size is sharp on every network's card. */
export const SHARE_IMAGE = { width: 1200, height: 630 } as const;

/** The largest centred 1.91:1 box inside an image of this size, as the source rectangle to draw. */
export function shareCrop(width: number, height: number): { x: number; y: number; width: number; height: number } {
  const ratio = SHARE_IMAGE.width / SHARE_IMAGE.height;
  const cropWidth = Math.min(width, height * ratio);
  const cropHeight = cropWidth / ratio;
  return { x: (width - cropWidth) / 2, y: (height - cropHeight) / 2, width: cropWidth, height: cropHeight };
}

/** Prepares a share image: the centre 1.91:1 of the picture, scaled to 1200x630 as a JPEG. */
export async function prepareShareImage(file: File): Promise<Blob> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new PhotoError("This image type can’t be read here. Try a JPEG or PNG.");
  }
  try {
    const box = shareCrop(bitmap.width, bitmap.height);
    const scale = Math.min(1, SHARE_IMAGE.width / box.width);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(box.width * scale));
    canvas.height = Math.max(1, Math.round(box.height * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new PhotoError("This image couldn’t be prepared.");
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, box.x, box.y, box.width, box.height, 0, 0, canvas.width, canvas.height);
    const image = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.86));
    if (!image || image.size > MAX_UPLOAD_BYTES) throw new PhotoError("This image couldn’t be prepared.");
    return image;
  } finally {
    bitmap.close();
  }
}
